/**
 * UndoConfirmDuplicateButton — Phase R10.4b (2026-07-05)
 *
 * Time-boxed owner affordance to unwind a `confirmed_duplicate`
 * rejection within 24 hours of the decision.
 *
 * Renders ONLY when ALL hold:
 *   - claim.status                   === 'rejected'
 *   - claim.owner_duplicate_decision === 'confirmed_duplicate'
 *   - claim.owner_duplicate_decision_at is a parseable ISO timestamp
 *   - (now - decided_at) < 24 hours
 *
 * UX:
 *   - Button labelled "Undo Confirm Duplicate"
 *   - Below the button: "Available for Xh Ym" countdown
 *     (re-computed every 60s; red when < 1h remaining)
 *   - Click → confirm modal with REQUIRED reason textarea
 *   - On submit → POST /undo-confirm-duplicate
 *   - On 400 undo_not_allowed → modal shows an inline error
 *     "This decision can no longer be undone (24-hour window expired)."
 *     and the countdown re-evaluates (which will hide the whole button).
 *
 * Props:
 *   claim                  — current claim object
 *   onUndo(reason)         — async handler supplied by parent; returns
 *                            a thenable that may reject with
 *                            `{ code: 'undo_not_allowed', message }` so
 *                            the modal can inline-surface the error.
 *   pending                — bool, mutation in-flight
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import { RotateCcw, X, Loader2, AlertTriangle } from 'lucide-react';

const UNDO_WINDOW_MS = 24 * 60 * 60 * 1000;
const REASON_MAX     = 500;

function parseIso(value) {
  if (!value) return null;
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? null : d;
}

function fmtRemaining(ms) {
  if (ms <= 0) return 'Expired';
  const totalMin = Math.floor(ms / 60_000);
  const h = Math.floor(totalMin / 60);
  const m = totalMin % 60;
  if (h <= 0) return `${m}m`;
  return `${h}h ${m}m`;
}

export default function UndoConfirmDuplicateButton({
  claim,
  onUndo,
  pending = false,
}) {
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState('');
  const [inlineErr, setInlineErr] = useState(null);
  // Minute-ticking clock so the countdown refreshes without a parent
  // refetch. Not expensive — one setInterval per mounted button.
  const [now, setNow] = useState(() => Date.now());
  const intervalRef = useRef(null);

  const decidedAt = useMemo(
    () => parseIso(claim?.owner_duplicate_decision_at),
    [claim?.owner_duplicate_decision_at],
  );
  const expiresAt = decidedAt ? decidedAt.getTime() + UNDO_WINDOW_MS : null;
  const remainingMs = expiresAt ? expiresAt - now : -1;

  const eligible =
    claim?.status === 'rejected'
    && claim?.owner_duplicate_decision === 'confirmed_duplicate'
    && decidedAt != null
    && remainingMs > 0;

  useEffect(() => {
    if (!eligible) {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
      return undefined;
    }
    intervalRef.current = setInterval(() => setNow(Date.now()), 60_000);
    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, [eligible]);

  if (!eligible) return null;

  const underHour = remainingMs < 60 * 60 * 1000;
  const remainingLabel = fmtRemaining(remainingMs);
  const trimmed = reason.trim();
  const canSubmit = !pending && trimmed.length > 0;

  const close = () => {
    if (pending) return;
    setOpen(false);
    setReason('');
    setInlineErr(null);
  };

  const submit = async () => {
    if (!canSubmit) return;
    setInlineErr(null);
    try {
      const result = onUndo(trimmed);
      if (result && typeof result.then === 'function') {
        await result;
      }
      close();
    } catch (err) {
      const code = err?.detail?.code ?? err?.code ?? null;
      if (code === 'undo_not_allowed') {
        setInlineErr(
          err?.detail?.message
            ?? 'This decision can no longer be undone (24-hour window expired).',
        );
      } else {
        setInlineErr(
          err?.detail?.message ?? err?.message ?? 'Could not undo the decision.',
        );
      }
    }
  };

  return (
    <div className="flex flex-col gap-1" data-testid="undo-confirm-duplicate">
      <button
        type="button"
        onClick={() => setOpen(true)}
        disabled={pending}
        data-testid="undo-confirm-duplicate-btn"
        className="inline-flex self-start items-center gap-2 bg-transparent border border-white/20 text-white/85 font-semibold rounded-xl px-4 py-2 text-sm hover:border-gold/60 hover:text-white disabled:opacity-50 transition-colors min-h-[36px]"
      >
        {pending ? <Loader2 size={14} className="animate-spin" /> : <RotateCcw size={14} />}
        Undo Confirm Duplicate
      </button>
      <span
        className={
          'text-[11px] font-medium ' +
          (underHour ? 'text-red-300' : 'text-white/55')
        }
        data-testid="undo-confirm-duplicate-countdown"
      >
        Available for {remainingLabel}
      </span>

      {open ? (
        <div
          className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4"
          data-testid="undo-confirm-duplicate-modal"
          onClick={pending ? undefined : close}
        >
          <div
            className="w-full max-w-lg rounded-3xl border border-white/10 bg-[#15151B] p-6 shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-start justify-between mb-3">
              <div>
                <div className="text-[11px] uppercase tracking-wider text-gold mb-1">
                  Undo Decision
                </div>
                <h2 className="text-xl font-bold text-white">
                  Undo Confirm Duplicate
                </h2>
              </div>
              <button
                type="button"
                onClick={close}
                disabled={pending}
                data-testid="undo-confirm-duplicate-modal-close"
                className="p-1 rounded-lg text-white/50 hover:text-white hover:bg-white/5 disabled:opacity-40"
              >
                <X size={16} />
              </button>
            </div>

            <p className="text-sm text-white/75 leading-relaxed mb-4">
              This will restore the claim to Manual Review. Duplicate
              validation will re-run. If the claim is still flagged, you&apos;ll
              need to decide again. Stamps are not affected.
            </p>

            <label className="block mb-2 text-xs font-semibold text-white/70">
              Reason <span className="text-red-300">*</span>
              <span className="text-white/40 font-normal ml-2">
                {reason.length}/{REASON_MAX}
              </span>
            </label>
            <textarea
              value={reason}
              onChange={(e) => setReason(e.target.value.slice(0, REASON_MAX))}
              rows={3}
              placeholder="Why are you undoing this decision? (required)"
              data-testid="undo-confirm-duplicate-modal-reason-input"
              className="w-full rounded-xl bg-white/5 border border-white/10 px-3 py-2 text-sm text-white placeholder-white/35 focus:border-gold/60 focus:outline-none resize-none"
            />

            {inlineErr ? (
              <div
                className="mt-3 flex items-start gap-2 rounded-xl border border-red-500/35 bg-red-500/10 p-3 text-xs text-red-100"
                data-testid="undo-confirm-duplicate-modal-error"
              >
                <AlertTriangle size={13} className="text-red-300 mt-0.5 shrink-0" />
                <span>{inlineErr}</span>
              </div>
            ) : null}

            <div className="flex items-center justify-end gap-2 pt-5">
              <button
                type="button"
                onClick={close}
                disabled={pending}
                data-testid="undo-confirm-duplicate-modal-cancel"
                className="px-4 py-2 text-sm text-white/70 hover:text-white disabled:opacity-40"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={submit}
                disabled={!canSubmit}
                data-testid="undo-confirm-duplicate-modal-submit"
                className="inline-flex items-center gap-2 px-4 py-2 text-sm font-semibold rounded-xl bg-gold hover:bg-gold-light text-black disabled:opacity-40 disabled:cursor-not-allowed"
              >
                {pending ? <Loader2 size={14} className="animate-spin" /> : null}
                Undo Decision
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}
